import React from 'react';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

/**
 * Commonly used layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.mainColumnClassName add more style rules in addition to css.main
 * @param {string?} props.sideNavClassName add more style rules in addition to css.sideNav
 * @param {ReactNode} props.children
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.sideNav
 * @param {ReactNode?} props.footer
 * @param {boolean?} props.useAccountSettingsNav
 * @param {boolean?} props.useProfileSideRail
 * @param {string?} props.currentPage
 * @returns {JSX.Element} LayoutComposer that expects children to be a function.
 */
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    useSettingsNav,
    useProfileSideRail,
    accountSettingsNavProps,
    intl,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = classNames(
    containerClassName || css.container,
    (useAccountSettingsNav || useSettingsNav) && css.containerAccountSettings,
    useProfileSideRail && css.containerProfile
  );

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
            </Topbar>
            <Main as="div" className={containerClasses}>
              <aside
                className={classNames(
                  css.sideNav,
                  (useAccountSettingsNav || useSettingsNav) && css.sideNavAccountSettings,
                  useProfileSideRail && css.sideNavProfile,
                  sideNavClassName
                )}
              >
                {useAccountSettingsNav ? (
                  <LayoutWrapperAccountSettingsSideNav
                    accountSettingsNavProps={accountSettingsNavProps}
                    ariaLabel={intl.formatMessage({
                      id: 'LayoutSideNavigation.screenreader.accountNavigation',
                    })}
                  />
                ) : null}
                {sideNavContent}
              </aside>
              <main
                id="main-content"
                className={classNames(
                  css.main,
                  (useAccountSettingsNav || useSettingsNav) && css.mainAccountSettings,
                  useProfileSideRail && css.mainProfile,
                  mainColumnClassName
                )}
              >
                {children}
              </main>
            </Main>
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

export default LayoutSideNavigation;
